<!--
Copyright (c) 2026 Toni Tone, LLC
-->
<script setup>
import Chart from 'chart.js/auto'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useCollapsibleSections, useDrillDown, useHelpModal, usePersistedFilters, useSortable, useUrlSync } from '../composables/composables'
import { chartColors, getColor } from '../utils/chartUtils'
import { debounce } from '../utils/debounce'
import { fetchData } from '../utils/fetchData'
import { formatDate, formatNumber } from '../utils/format'
import { useMapDisplay } from '../utils/mapUtils'
import { buildDateClause, buildSearchClause, buildTextClause } from '../utils/queryBuilder'
import { decodeOp, encodeOp, setHashParams } from '../utils/urlFilters'

const helpModal = useHelpModal()
const { initializeMap, displayLocation, displayAllLocations, geocodeAddress, destroyMap, invalidateSize } = useMapDisplay('food-map')
const { showCharts, showMap, showTable, notifyMapVisible } = useCollapsibleSections(invalidateSize)
const { sortField, sortDirection, sortBy, getSortIndicator, sortRecords } = useSortable()

const records = ref([])
const loading = ref(false)
const error = ref('')
const searchQuery = ref('')
const totalRecords = ref(0)
const failedInspections = ref(0)
const passedInspections = ref(0)
const offset = ref(0)
const selectedRecord = ref(null)
const limit = ref(25)
const filters = ref({
  fromDate: '',
  toDate: '',
})

const fieldOptions = [
  { label: 'Business Name', value: 'businessname' },
  { label: 'Violation Status', value: 'viol_status' },
  { label: 'Violation Level', value: 'viol_level' },
  { label: 'Violation', value: 'violdesc' },
  { label: 'City', value: 'city' }
]

const selectedField = ref(fieldOptions[0])
const selectedOperator = ref('=')
const filterValue = ref('')

let isInitialMount = true

const resourceId = '4582bec6-2b4f-4f9e-bc55-cbaa73117f4c'

const { restore, clear } = usePersistedFilters('food', {
  searchQuery, filters, filterValue, selectedField, selectedOperator, limit, fieldOptions
})

const sortedRecords = computed(() => {
  if (!sortField.value) return records.value
  return sortRecords(records.value, sortField.value)
})

const currentPage = computed(() => Math.floor(offset.value / limit.value) + 1)
const totalPages = computed(() => Math.ceil(totalRecords.value / limit.value))
const startRecord = computed(() => totalRecords.value === 0 ? 0 : offset.value + 1)
const endRecord = computed(() => Math.min(offset.value + limit.value, totalRecords.value))

const pageOptions = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) {
    pages.push(i)
  }
  return pages
})

const displayedPins = computed(() => {
  return records.value.length > 200 ? Math.min(100, records.value.length) : records.value.length
})

const selectRecord = (record) => {
  selectedRecord.value = record
}

const calculateStats = () => {
  failedInspections.value = records.value.filter(r => r.viol_status === 'Fail').length
  passedInspections.value = records.value.filter(r => r.result && r.result.toLowerCase().includes('pass')).length
}

const fetchInspections = async () => {
  const clauses = []
  const dateClause = buildDateClause('resultdttm', filters.value.fromDate, filters.value.toDate)
  clauses.push(...dateClause)

  if (filterValue.value) {
    const clause = buildTextClause(selectedField.value.value, selectedOperator.value, filterValue.value)
    if (clause) clauses.push(clause)
  }

  if (searchQuery.value) {
    const sc = buildSearchClause(['businessname'], searchQuery.value)
    if (sc) clauses.push(sc)
  }

  await fetchData({
    endpoint: '/api/boston-food',
    records,
    loading,
    error,
    totalRecords,
    offset,
    limit: limit.value,
    sqlConfig: { resourceId, clauses },
    orderBy: 'resultdttm',
    errorPrefix: 'food inspections',
    onSuccess: calculateStats
  })
}

const handleSearch = () => { offset.value = 0; fetchInspections() }
const handleSQLSearch = handleSearch

const goToPage = (page) => {
  offset.value = (page - 1) * limit.value
  fetchInspections()
}

const firstPage = () => goToPage(1)
const previousPage = () => goToPage(currentPage.value - 1)
const nextPage = () => goToPage(currentPage.value + 1)
const lastPage = () => goToPage(totalPages.value)

const onPageSelect = (e) => {
  goToPage(Number(e.target.value))
}

const onLimitChange = () => {
  offset.value = 0
  fetchInspections()
}

// Auto-search when filter field or operator changes
watch([selectedField, selectedOperator], () => {
  if (isInitialMount) return
  offset.value = 0
  fetchInspections()
})

const resetFilters = () => {
  clear()
  searchQuery.value = ''
  filters.value.fromDate = ''
  filters.value.toDate = ''
  filterValue.value = ''
  selectedField.value = fieldOptions[0]
  selectedOperator.value = '='
  limit.value = 25
  offset.value = 0
  setHashParams({})
  fetchInspections()
}

let drillDownInstance = useDrillDown(fieldOptions, searchQuery, filters, selectedField, selectedOperator, filterValue, offset, fetchInspections)
let { drillDown: drillDownFn } = drillDownInstance

// Wrapper to ensure selectedField is always properly set when drill-down is triggered
const drillDown = (fieldName, value) => {
  // Ensure the field object is set in selectedField
  const field = fieldOptions.find(f => f.value === fieldName)
  if (field) {
    selectedField.value = field
  }
  // Call the actual drillDown function
  drillDownFn(fieldName, value)
}

// Chart Logic
const resultRef = ref(null)
const violationRef = ref(null)
const cityRef = ref(null)
let resultChart, violationChart, cityChart

const aggregateByStatus = computed(() => {
  const map = new Map()
  records.value.forEach(r => {
    const key = r.viol_status || 'Unknown'
    map.set(key, (map.get(key) || 0) + 1)
  })
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
})

const aggregateByViolation = computed(() => {
  const map = new Map()
  records.value.forEach(r => {
    const key = r.violdesc || 'No Violation'
    map.set(key, (map.get(key) || 0) + 1)
  })
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
})

const aggregateByCity = computed(() => {
  const map = new Map()
  records.value.forEach(r => {
    const key = r.city || 'Unknown'
    map.set(key, (map.get(key) || 0) + 1)
  })
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
})

const renderCharts = async () => {
  if (loading.value || records.value.length === 0) return
  await nextTick()

  if (resultChart) resultChart.destroy()
  if (violationChart) violationChart.destroy()
  if (cityChart) cityChart.destroy()

  // Result Pie
  if (resultRef.value) {
    const data = aggregateByStatus.value
    resultChart = new Chart(resultRef.value, {
      type: 'doughnut',
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          data: data.map(d => d.count),
          backgroundColor: data.map((_, i) => getColor(i))
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'right' } }
      }
    })
  }

  // Violation Bar
  if (violationRef.value) {
    const data = aggregateByViolation.value.slice(0, 10)
    violationChart = new Chart(violationRef.value, {
      type: 'bar',
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          label: 'Count',
          data: data.map(d => d.count),
          backgroundColor: chartColors.red
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } }
      }
    })
  }

  // City Bar
  if (cityRef.value) {
    const data = aggregateByCity.value.slice(0, 10)
    cityChart = new Chart(cityRef.value, {
      type: 'bar',
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          label: 'Inspections',
          data: data.map(d => d.count),
          backgroundColor: chartColors.teal
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } }
      }
    })
  }
}

const displayAllPins = async () => {
  const recordsToDisplay = records.value.length > 200 ? records.value.slice(0, 100) : records.value
  const direct = []
  const fallback = new Map()
  for (const r of recordsToDisplay) {
    const label = `${r.businessname || ''}, ${r.city || ''}`
    const match = r.location?.match(/\(([^,]+),\s*([^)]+)\)/)
    const lat = match ? parseFloat(match[1]) : NaN
    const lon = match ? parseFloat(match[2]) : NaN
    if (!isNaN(lat) && !isNaN(lon) && !(lat === 0 && lon === 0)) {
      direct.push({ lat, lon, label })
    } else {
      const addr = r.address ? `${r.address}, ${r.city || 'Boston'}, MA` : null
      if (addr && !fallback.has(addr)) fallback.set(addr, label)
    }
  }
  const markers = [...direct]
  let i = 0
  for (const [addr, label] of fallback) {
    const coords = await geocodeAddress(addr)
    if (coords) markers.push({ lat: coords.lat, lon: coords.lon, label })
    if (i < fallback.size - 1) await new Promise(r => setTimeout(r, 250))
    i++
  }
  displayAllLocations(markers)
}

watch([records, loading], () => {
  renderCharts()
  if (!loading.value && records.value.length > 0) {
    selectedRecord.value = null
    notifyMapVisible()
    displayAllPins()
  }
})

const readUrlParams = useUrlSync(
  () => ({
    q: searchQuery.value || undefined,
    field: selectedField.value?.value !== fieldOptions[0].value ? selectedField.value?.value : undefined,
    op: selectedOperator.value !== '=' ? encodeOp(selectedOperator.value) : undefined,
    val: filterValue.value || undefined,
    from: filters.value.fromDate || undefined,
    to: filters.value.toDate || undefined,
    limit: limit.value !== 25 ? String(limit.value) : undefined,
  }),
  [searchQuery, selectedField, selectedOperator, filterValue, filters, limit]
)

onMounted(() => {
  restore()
  initializeMap()
  readUrlParams(p => {
    if (p.q) searchQuery.value = p.q
    if (p.field) {
      const match = fieldOptions.find(f => f.value === p.field)
      if (match) selectedField.value = match
    }
    if (p.op) selectedOperator.value = decodeOp(p.op)
    if (p.val) filterValue.value = p.val
    if (p.from) filters.value.fromDate = p.from
    if (p.to) filters.value.toDate = p.to
    if (p.limit) limit.value = Number(p.limit)
  })
  isInitialMount = false
  fetchInspections()
})

onUnmounted(() => {
  destroyMap()
})

const debouncedDisplay = debounce((lat, lon, label, address) => {
  displayLocation(lat, lon, label, address)
}, 300)

watch(selectedRecord, (newRecord) => {
  if (!newRecord) {
    displayAllPins()
    return
  }

  // Parse location string "(lat, lon)"
  if (newRecord.location) {
    const match = newRecord.location.match(/\(([^,]+),\s*([^)]+)\)/)
    if (match) {
      const lat = parseFloat(match[1])
      const lon = parseFloat(match[2])
      const label = `${newRecord.businessname}, ${newRecord.city}`

      if (!isNaN(lat) && !isNaN(lon)) {
        displayLocation(lat, lon, label)
        return
      }
    }
  }

  // Fallback to geocoding
  if (newRecord.address) {
    const label = `${newRecord.businessname}, ${newRecord.city}`
    const fullAddress = `${newRecord.address}, ${newRecord.city}, MA`
    debouncedDisplay(null, null, label, fullAddress)
  }
})
</script>

<template>
  <div class="data-explorer">
    <div class="explorer-header">
      <h1>Food Establishment Inspections</h1>
      <p class="subtitle">Health inspections of food establishments in Boston</p>
      <button @click="helpModal.openHelpModal('Food Establishment Inspections', 'https://data.boston.gov/dataset/food-establishment-inspections', 'Records of health and safety inspections conducted at food establishments in Boston, including violations and compliance status.')" class="help-btn">?</button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-label">Total Records in Current Search</div>
        <div class="stat-value">{{ formatNumber(totalRecords) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Failed Inspections (Current Page)</div>
        <div class="stat-value">{{ failedInspections }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Passed Inspections (Current Page)</div>
        <div class="stat-value">{{ passedInspections }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Pass Rate (Current Page)</div>
        <div class="stat-value">{{ records.length > 0 ? Math.round((passedInspections / records.length) * 100) : 0 }}%</div>
      </div>
    </div>

    <div class="filters">
      <div class="filter-group">
        <label>Records per page:</label>
        <select v-model.number="limit" @change="onLimitChange">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="200">200</option>
          <option :value="400">400</option>
          <option :value="750">750</option>
          <option :value="1000">1000</option>
          <option :value="2000">2000</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Text Search:</label>
        <input v-model="searchQuery" placeholder="Search by business name..." @keyup.enter="handleSearch" />
      </div>

      <div class="filter-group">
        <label for="from-date">From Date:</label>
        <input id="from-date" type="date" v-model="filters.fromDate" @change="handleSQLSearch" />
      </div>

      <div class="filter-group">
        <label for="to-date">To Date:</label>
        <input id="to-date" type="date" v-model="filters.toDate" @change="handleSQLSearch" />
      </div>

      <div class="filter-group">
        <label>Advanced Filter:</label>
        <select v-model="selectedField">
          <option v-for="f in fieldOptions" :key="f.value" :value="f">{{ f.label }}</option>
        </select>
        <select v-model="selectedOperator">
          <option value="=">=</option>
          <option value="!=">!=</option>
          <option value="LIKE">Contains</option>
        </select>
        <input v-model="filterValue" placeholder="Value..." @keyup.enter="handleSQLSearch" />
      </div>

      <div class="button-group">
        <button @click="resetFilters" class="page-btn reset-btn">Reset Filters</button>
      </div>
    </div>

    <div v-if="loading" class="loading"><div class="spinner"></div></div>
    <div v-else-if="error" class="error"><p>{{ error }}</p><button @click="handleSearch" class="page-btn">Retry</button></div>

    <div v-show="!loading && !error && records.length > 0" class="content-container">
      <!-- Collapsible Graphs Section -->
      <div class="section-header" @click="showCharts = !showCharts">
        <span class="chevron">{{ showCharts ? '▼' : '▶' }}</span>
        <h2>Charts & Analytics</h2>
      </div>
          <div v-show="showCharts" class="charts-grid">
            <div class="chart-card">
              <h3>Inspection Results</h3>
              <canvas ref="resultRef"></canvas>
            </div>
            <div class="chart-card">
              <h3>Top Violations</h3>
              <canvas ref="violationRef"></canvas>
            </div>
            <div class="chart-card">
              <h3>Inspections by Neighborhood</h3>
              <canvas ref="cityRef"></canvas>
            </div>
          </div>

          <!-- Collapsible Map Section -->
      <div class="section-header" @click="showMap = !showMap">
        <span class="chevron">{{ showMap ? '▼' : '▶' }}</span>
        <h2>Location Map</h2>
      </div>
          <div v-show="showMap" class="map-section">
            <div class="map-header">
              <p v-if="selectedRecord" class="selected-info">
                Selected: {{ selectedRecord.businessname }}, {{ selectedRecord.city }}
              </p>
              <p v-else class="instruction-text">Showing location{{ displayedPins !== 1 ? 's' : '' }} from {{ displayedPins }} record{{ displayedPins !== 1 ? 's' : '' }} ({{ records.length }} total) — select a row to focus</p>
            </div>
            <div id="food-map" class="map-container"></div>
          </div>

          <!-- Collapsible Table Section -->
      <div class="section-header" @click="showTable = !showTable">
        <span class="chevron">{{ showTable ? '▼' : '▶' }}</span>
        <h2>Inspection Records</h2>
      </div>
      <div v-show="showTable" class="table-section">
        <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th @click="sortBy('businessname')" style="cursor: pointer;">
                    Business {{ getSortIndicator('businessname') }}
                  </th>
                  <th @click="sortBy('address')" style="cursor: pointer;">
                    Address {{ getSortIndicator('address') }}
                  </th>
                  <th @click="sortBy('viol_level')" style="cursor: pointer;">
                    Violation Level {{ getSortIndicator('viol_level') }}
                  </th>
                  <th @click="sortBy('violdesc')" style="cursor: pointer;">
                    Violation {{ getSortIndicator('violdesc') }}
                  </th>
                  <th @click="sortBy('viol_status')" style="cursor: pointer;">
                    Status {{ getSortIndicator('viol_status') }}
                  </th>
                  <th @click="sortBy('resultdttm')" style="cursor: pointer;">
                    Inspection Date {{ getSortIndicator('resultdttm') }}
                  </th>
                  <th @click="sortBy('comments')" style="cursor: pointer;">
                    Comments {{ getSortIndicator('comments') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="record in sortedRecords" 
                  :key="record._id"
                  @click="selectRecord(record)"
                  :class="{ 'selected-row': selectedRecord?._id === record._id }"
                  class="clickable-row"
                >
                  <td data-label="Business"><span class="drilldown" @click.stop="drillDown('businessname', record.businessname)">{{ record.businessname }}</span></td>
                  <td data-label="Address">{{ record.address }}</td>
                  <td data-label="Violation Level"><span class="drilldown" @click.stop="drillDown('viol_level', record.viol_level)">{{ record.viol_level }}</span></td>
                  <td data-label="Violation"><span class="drilldown" @click.stop="drillDown('violdesc', record.violdesc)">{{ record.violdesc || 'N/A' }}</span></td>
                  <td data-label="Status" :class="{ 'fail-cell': record.viol_status === 'Fail' }">
                    <span class="drilldown" @click.stop="drillDown('viol_status', record.viol_status)">{{ record.viol_status }}</span>
                  </td>
                  <td data-label="Inspection Date">{{ formatDate(record.resultdttm) }}</td>
                  <td data-label="Comments">{{ record.comments || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>

            <div class="pagination" v-if="totalRecords > 0">
              <button 
                :disabled="currentPage === 1" 
                @click="firstPage" 
                class="page-btn"
                title="First Page"
              >
                « First
              </button>
              <button 
                :disabled="currentPage === 1" 
                @click="previousPage" 
                class="page-btn"
                title="Previous Page"
              >
                ‹ Previous
              </button>
              
              <span class="pagination-info">
                Showing {{ formatNumber(startRecord) }} - {{ formatNumber(endRecord) }} of {{ formatNumber(totalRecords) }} records
              </span>
              
              <select 
                :value="currentPage" 
                @change="onPageSelect" 
                class="page-select"
                title="Select Page"
              >
                <option v-for="page in pageOptions" :key="page" :value="page">
                  Page {{ page }} of {{ totalPages }}
                </option>
              </select>
              
              <button 
                :disabled="currentPage === totalPages" 
                @click="nextPage" 
                class="page-btn"
                title="Next Page"
              >
                Next ›
              </button>
              <button 
                :disabled="currentPage === totalPages" 
                @click="lastPage" 
                class="page-btn"
                title="Last Page"
              >
                Last »
              </button>
            </div>
          </div>
      </div>
    </div>

    <div v-if="!loading && !error && records.length === 0" class="empty-state"><p>No records found matching your filters.</p></div>

    <div v-if="helpModal.showModal.value" class="modal-overlay" @click="helpModal.closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ helpModal.modalContent.value.title }}</h2>
          <button @click="helpModal.closeModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <p>{{ helpModal.modalContent.value.description }}</p>
          <a :href="helpModal.modalContent.value.url" target="_blank" class="dataset-link">View Dataset on boston.gov →</a>
        </div>
      </div>
    </div>
  </div>
</template>
